import {type Size, Utils, PlayerError} from '@clappr/core'
import {loadScript} from 'utils/dom'
import {isDebug} from 'utils/env'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import {
  type AdvertisementSource,
  type AdvertisementVastSource,
  AdvertisementProvider,
  AdvertisementType
} from 'common/api/records'
import {UNKNOWN_ID} from 'common/constants'
import {AdPlayback} from '../ad-playback'
import {addSspUrlParams} from '../vast-playback/ad-systems'

export interface ImaEvent {
  getAd: () => Record<string, unknown>
  getAdData: () => Record<string, unknown>
}

export interface ImaError {
  getErrorCode: () => number
  getType: () => string
  getMessage: () => string
}

export interface ImaErrorEvent {
  getError: () => ImaError
}

export class ImaPlayback extends AdPlayback<AdvertisementVastSource> {
  public static VAST_LOAD_TIMEOUT = 8000
  public static LOAD_VIDEO_TIMEOUT = 10000
  private static preloadPromise?: Promise<any>

  public static canPlay(source: AdvertisementSource): boolean {
    return (
      !!window.google?.ima &&
      source.provider === AdvertisementProvider.VAST &&
      !!source.url
    )
  }

  public static async preload(_: Options): Promise<any> {
    if (window.google?.ima) return

    ImaPlayback.preloadPromise ??= (async () => {
      try {
        await loadScript(
          `//imasdk.googleapis.com/js/sdkloader/${
            isDebug() ? 'ima3_debug' : 'ima3'
          }.js`
        )
        window.google?.ima.settings.setPlayerType(process.env.PACKAGE_NAME)
        window.google?.ima.settings.setPlayerVersion(process.env.VERSION)
        window.google?.ima.settings.setLocale(Utils.getBrowserLanguage())
        window.google?.ima.settings.setVpaidMode(
          window.google.ima.ImaSdkSettings.VpaidMode.INSECURE
        )
      } catch {
        delete ImaPlayback.preloadPromise
      }
    })()

    return await ImaPlayback.preloadPromise
  }

  public static destroy(): void {
    ImaPlayback.adDisplayContainer?.destroy()
    delete ImaPlayback.adDisplayContainer
  }

  protected static adDisplayContainer: any = null

  protected get adDisplayContainer(): any {
    return ImaPlayback.adDisplayContainer
  }

  protected set adDisplayContainer(adDisplayContainer: any) {
    ImaPlayback.adDisplayContainer = adDisplayContainer
  }

  protected ad?: any
  protected adsRequest?: any
  protected adsLoader?: any
  protected adsManager?: any
  protected currentTime = 0

  public get allowBlock(): boolean | undefined {
    return false
  }

  public get reloadFailedAdTag(): boolean | undefined {
    return false
  }

  public get name(): string {
    return 'imaPlayback'
  }

  protected get ima(): any {
    return window.google?.ima
  }

  public async load(source: AdvertisementVastSource): Promise<void> {
    source.url = source.url.replace(/\[sessionid\]/g, Utils.uniqueId(''))

    if (source.url.match('ssp.rambler.ru')) {
      source.url =
        (await addSspUrlParams(source.url, this.core.options)) ?? source.url
    }

    this.sources = source
    this.createAdDisplayContainer()
    this.createAdsRequest()
    await this.loadAdsManager()
    this.initAdsManager()
  }

  protected createAdDisplayContainer(): void {
    if (!this.adDisplayContainer) {
      const {containerElement, videoElement, clickTrackingElement} =
        this.options

      this.adDisplayContainer = new this.ima.AdDisplayContainer(
        containerElement,
        videoElement,
        clickTrackingElement
      )
      this.adDisplayContainer.initialize()
    }
  }

  protected createAdsRequest(): void {
    const {videoElement, muted} = this.options

    this.adsRequest = new this.ima.AdsRequest()
    this.adsRequest.adTagUrl = this.sources.url
    this.adsRequest.vastLoadTimeout = ImaPlayback.VAST_LOAD_TIMEOUT
    this.adsRequest.linearAdSlotWidth = videoElement.offsetWidth
    this.adsRequest.linearAdSlotHeight = videoElement.offsetHeight
    this.adsRequest.nonLinearAdSlotWidth = videoElement.offsetWidth
    this.adsRequest.nonLinearAdSlotHeight = videoElement.offsetHeight
    // assume playback is consented by user
    this.adsRequest.setAdWillAutoPlay(true)
    this.adsRequest.setAdWillPlayMuted(muted)
  }

  protected async loadAdsManager(): Promise<void> {
    let adsManagerLoadedEvent: any

    try {
      this.adsLoader = new this.ima.AdsLoader(this.adDisplayContainer)
      adsManagerLoadedEvent = await new Promise((resolve, reject) => {
        this.adsLoader.addEventListener(
          this.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          resolve
        )
        this.adsLoader.addEventListener(
          this.ima.AdErrorEvent.Type.AD_ERROR,
          reject
        )
        this.adsLoader.requestAds(this.adsRequest)
      })
    } catch (error: any) {
      this.onAdError(error)

      return
    }

    const adsRenderingSettings = new this.ima.AdsRenderingSettings()

    // adsRenderingSettings.useVideoAdUi = !this.vast.withCustomUi
    // plugin will handle playback state when ad has completed
    adsRenderingSettings.loadVideoTimeout = ImaPlayback.LOAD_VIDEO_TIMEOUT
    adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = false
    this.adsManager = adsManagerLoadedEvent.getAdsManager(
      this.options.videoElement,
      adsRenderingSettings
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.LOADED,
      (event: ImaEvent) => this.onAdLoaded(event)
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      this.onAdContentResumeRequested
    )
    this.adsManager.addEventListener(
      this.ima.AdErrorEvent.Type.AD_ERROR,
      this.onAdError
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.STARTED,
      this.onAdStarted
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.IMPRESSION,
      this.onAdImpression
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.AD_PROGRESS,
      (event: ImaEvent) => this.onAdProgress(event)
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.VOLUME_CHANGED,
      this.onAdVolumeChange
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.VOLUME_MUTED,
      this.onAdVolumeChange
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.PAUSED,
      this.onAdPaused
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.RESUMED,
      this.onAdResumed
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.COMPLETED,
      this.onAdComplete
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.USER_CLOSE,
      this.onAdUserClose
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.CLICK,
      (event: ImaEvent) => this.onAdClick(event)
    )
    this.adsManager.addEventListener(
      this.ima.AdEvent.Type.SKIPPED,
      (event: ImaEvent) => this.onAdSkipped(event)
    )
  }

  protected initAdsManager(): void {
    if (this.adsManager) {
      const {videoElement} = this.options

      this.adsManager.init(
        videoElement.offsetWidth,
        videoElement.offsetHeight,
        this.ima.ViewMode.NORMAL
      )
      this.trigger(Events.ADPLAYBACK_READY, this.sources)
    }
  }

  protected onAdLoaded(event: ImaEvent): void {
    this.ad = event.getAd()
    this.trigger(Events.ADPLAYBACK_LOADED, this.sources)

    if (!this.isLinear()) {
      const videoElement = this.options.videoElement as HTMLMediaElement

      videoElement.play()
    }
  }

  protected onAdStarted = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_STARTED, this.sources)
  }

  protected onAdImpression = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_IMPRESSION, this.sources)
  }

  protected onAdProgress(event: ImaEvent): void {
    const {duration = 0, currentTime = 0} = event.getAdData()

    this.currentTime = currentTime as number

    this.trigger(Events.ADPLAYBACK_PROGRESS, {
      ...this.sources,
      current: currentTime,
      total: duration
    })
  }

  protected onAdPaused = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_PAUSED, this.sources)
  }

  protected onAdResumed = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_RESUMED, this.sources)
  }

  protected onAdComplete = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_COMPLETE, this.sources)
  }

  protected onAdUserClose = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_USER_CLOSE, this.sources)
  }

  protected onAdVolumeChange = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_VOLUME_CHANGE, this.sources)
  }

  protected onAdClick(_event: ImaEvent): void {
    this.trigger(Events.ADPLAYBACK_CLICK, this.sources)
  }

  protected onAdSkipped(_event: ImaEvent): void {
    this.trigger(Events.ADPLAYBACK_SKIPPED, this.sources)
  }

  protected onAdContentResumeRequested = (_event: ImaEvent): void => {
    this.trigger(Events.ADPLAYBACK_CONTENT_RESUME_REQUESTED, this.sources)
  }

  protected onAdError = (errorEvent: ImaErrorEvent): void => {
    const error = errorEvent.getError()
    const errorCode = error.getErrorCode()
    const canRefetch =
      errorCode === this.ima.AdError.ErrorCode.VIDEO_PLAY_ERROR ||
      errorCode === this.ima.AdError.ErrorCode.FAILED_TO_REQUEST_ADS ||
      errorCode === this.ima.AdError.ErrorCode.VAST_ASSET_MISMATCH ||
      errorCode === this.ima.AdError.ErrorCode.VAST_LINEAR_ASSET_MISMATCH ||
      errorCode === this.ima.AdError.ErrorCode.VAST_NONLINEAR_ASSET_MISMATCH ||
      errorCode === this.ima.AdError.ErrorCode.VAST_ASSET_NOT_FOUND ||
      errorCode === this.ima.AdError.ErrorCode.NONLINEAR_DIMENSIONS_ERROR ||
      errorCode === this.ima.AdError.ErrorCode.COMPANION_REQUIRED_ERROR ||
      errorCode === this.ima.AdError.ErrorCode.UNKNOWN_ERROR ||
      errorCode === this.ima.AdError.ErrorCode.AUTOPLAY_DISALLOWED

    const formattedError = this.createError({
      code: `${errorCode ?? UNKNOWN_ID}`,
      description: error.getMessage(),
      level: PlayerError.Levels.WARN,
      raw: {...this.sources, canRefetch, code: errorCode}
    })

    this.trigger(Events.ADPLAYBACK_ERROR, formattedError)
  }

  public start(): void {
    this.adsManager?.start()
  }

  public pause(): void {
    this.adsManager?.pause()
  }

  public resume(): void {
    this.adsManager?.resume()
  }

  public skip(): void {
    const isAdSkippableState = this.adsManager?.getAdSkippableState()

    if (isAdSkippableState) {
      this.adsManager?.skip()

      return
    }

    this.adsManager?.addEventListener(
      this.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
      (event: any) => this.onAdSkipped(event)
    )
    this.adsManager?.stop()
  }

  public setVolume(volume: number): void {
    this.adsManager?.setVolume(volume / 100)
  }

  public getVolume(): number {
    const volume = this.adsManager?.getVolume() ?? 0

    return volume * 100
  }

  public resize(size: Size): void {
    this.adsManager?.resize(size.width, size.height, this.ima.ViewMode.NORMAL)
  }

  public getDuration(): number {
    return this.ad?.getDuration() ?? 0
  }

  public getCurrentTime(): number {
    return this.currentTime
  }

  public isLinear(): boolean {
    return this.ad?.isLinear() ?? true
  }

  public getAdSystem(): string {
    return this.ad?.getAdSystem() ?? UNKNOWN_ID
  }

  public getAdType(): AdvertisementType {
    const apiFramework = this.ad?.getApiFramework()?.toLowerCase()

    if (!apiFramework) {
      return AdvertisementType.UNKNOWN
    }

    return apiFramework === 'vpaid'
      ? AdvertisementType.VPAID
      : AdvertisementType.VAST
  }

  public destroy(): void {
    this.adsLoader?.contentComplete()
    this.adsLoader?.destroy()
    this.adsManager?.destroy()
  }
}
